import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { ConselhoRubric } from '@/services/types';

/**
 * Member x dimension grid of the reasoning rubric: the adjusted author
 * effect (cumulative logit) with its 90% interval, the raw mean score in
 * the title. Dimensions come from the rows, never from a hardcoded list, so
 * a dimension the ETL drops for low inter-model agreement disappears with
 * it. Members below the floor are listed after the others and greyed,
 * never ranked into them. On a member's own page the member column is
 * dropped (`showMember={false}`): the page heading already names them.
 */
export function RubricTable({ rows, showMember = true }: { rows: ConselhoRubric[]; showMember?: boolean }) {
  const { t, i18n } = useTranslation(['anatel']);
  const signed = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const plain = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 });
  const integer = new Intl.NumberFormat(i18n.language);
  const dimensions = Array.from(new Set(rows.map((r) => r.dimension))).sort();
  const byMember = new Map<string, { slug: string; n: number; suppressed: boolean; cells: Map<string, ConselhoRubric> }>();
  for (const r of rows) {
    const entry = byMember.get(r.name) ?? { slug: r.slug, n: r.n, suppressed: r.suppressed, cells: new Map() };
    entry.n = Math.max(entry.n, r.n);
    entry.suppressed = entry.suppressed && r.suppressed;
    entry.cells.set(r.dimension, r);
    byMember.set(r.name, entry);
  }
  const members = Array.from(byMember.entries()).sort((a, b) => {
    if (a[1].suppressed !== b[1].suppressed) return a[1].suppressed ? 1 : -1;
    return b[1].n - a[1].n || a[0].localeCompare(b[0]);
  });
  const dimensionLabel = (d: string) => t(`anatel:conselho.rubric.dimensions.${d}`, d.replace(/_/g, ' '));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="rubric-table">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            {showMember ? <th className="p-2">{t('anatel:conselho.rubric.member')}</th> : null}
            <th className="p-2 text-right">{t('anatel:conselho.rubric.documents')}</th>
            {dimensions.map((d) => (
              <th key={d} className="p-2 text-right" title={dimensionLabel(d)}>
                {dimensionLabel(d)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {members.map(([name, m]) => (
            <tr key={name} className={`border-t ${m.suppressed ? 'text-muted-foreground' : ''}`} data-suppressed={m.suppressed ? 'true' : 'false'}>
              {showMember ? (
                <td className="p-2">
                  <Link to={`/anatel/r/${m.slug}`} className="underline-offset-4 hover:underline">
                    {name}
                  </Link>
                  {m.suppressed ? <span className="ml-1 text-[10px]">({t('anatel:conselho.rubric.belowFloor')})</span> : null}
                </td>
              ) : null}
              <td className="p-2 text-right tabular-nums">
                {integer.format(m.n)}
                {!showMember && m.suppressed ? <span className="ml-1 text-[10px]">({t('anatel:conselho.rubric.belowFloor')})</span> : null}
              </td>
              {dimensions.map((d) => {
                const cell = m.cells.get(d);
                if (!cell || cell.effect === null) {
                  return (
                    <td key={d} className="p-2 text-right text-muted-foreground">
                      —
                    </td>
                  );
                }
                const excludesZero = cell.lower !== null && cell.upper !== null && (cell.lower > 0 || cell.upper < 0);
                return (
                  <td
                    key={d}
                    className={`p-2 text-right tabular-nums ${excludesZero && !m.suppressed ? 'font-semibold' : ''}`}
                    title={`${t('anatel:conselho.rubric.rawMean')} ${cell.raw_mean !== null ? plain.format(cell.raw_mean) : '—'}; 90% [${cell.lower !== null ? signed.format(cell.lower) : '—'}, ${cell.upper !== null ? signed.format(cell.upper) : '—'}]`}
                    data-excludes-zero={excludesZero ? 'true' : 'false'}
                  >
                    {signed.format(cell.effect)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
